import React, { useState } from 'react';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { auth, firestore } from '../firebase.config';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import logo from './../image/logo.png';

const MainContainer = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const navigate = useNavigate();

  // ================= LOGIN =================
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const uname = username.trim().toLowerCase();

      let userDocRef = doc(firestore, 'users', uname);
      let userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        userDocRef = doc(firestore, 'Staff', uname);
        userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          setError('Invalid username or user does not exist.');
          return;
        }
      }

      const { email, role } = userDoc.data();

      await signInWithEmailAndPassword(auth, email, password);

      // Login history
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toLocaleTimeString();

      await updateDoc(userDocRef, {
        [`loginHistory.${date}`]: {
          LoginDate: date,
          LoginTime: time
        }
      });

      navigate('/homeslider');

    } catch (err) {
      if (
        err.code === "auth/wrong-password" ||
        err.code === "auth/user-not-found"
      ) {
        setError("Invalid username or password.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Try later.");
      } else {
        setError("Login failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ================= REGISTER =================
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const uname = username.trim().toLowerCase();

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      await setDoc(doc(firestore, 'users', uname), {
        username: uname,
        email,
        role: 'admin',
        userId: userCredential.user.uid,
        createdAt: new Date()
      });

      alert("Registration successful!");
      navigate('/homeslider');

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ================= FORGOT PASSWORD =================
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const uname = username.trim().toLowerCase();

      let userDocRef = doc(firestore, 'users', uname);
      let userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        userDocRef = doc(firestore, 'Staff', uname);
        userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          setError("User not found.");
          return;
        }
      }

      const { email } = userDoc.data();

      await sendPasswordResetEmail(auth, email);
      alert("Password reset email sent!");

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-gray-100 shadow-2xl rounded-3xl p-8">

        {/* Logo */}
        <div className="text-center mb-6">
          <img src={logo} alt="Logo" className="h-16 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-gray-800">
            {isRegistering ? 'Register Admin' : 'Admin Portal'}
          </h2>
          <p className="text-sm text-gray-500">
            {isRegistering ? 'Create your account' : 'Login to continue'}
          </p>
        </div>

        <form
          onSubmit={
            isResettingPassword
              ? handleForgotPassword
              : isRegistering
              ? handleRegister
              : handleLogin
          }
          className="space-y-5"
        >
          {error && (
            <div className="bg-red-50 text-red-500 text-sm p-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Username */}
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Username</label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#fcaf17] focus:border-[#fcaf17] outline-none"
                required
              />
            </div>
          </div>

          {/* Email */}
          {isRegistering && (
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#fcaf17] outline-none"
                required
              />
            </div>
          )}

          {/* Password */}
          {!isResettingPassword && (
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#fcaf17] outline-none"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}

          {/* Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#fcaf17] hover:bg-yellow-500 text-black font-semibold py-3 rounded-xl shadow-md transition duration-300 disabled:opacity-60"
          >
            {loading
              ? "Processing..."
              : isResettingPassword
              ? "Send Reset Link"
              : isRegistering
              ? "Register"
              : "Login"}
          </button>

          {/* Links */}
          <div className="text-center text-sm space-y-2">
            {!isRegistering && !isResettingPassword && (
              <p
                className="text-[#fcaf17] cursor-pointer font-medium hover:underline"
                onClick={() => setIsResettingPassword(true)}
              >
                Forgot Password?
              </p>
            )}

            {isResettingPassword && (
              <p
                className="text-[#fcaf17] cursor-pointer font-medium hover:underline"
                onClick={() => setIsResettingPassword(false)}
              >
                Back to Login
              </p>
            )}

            {!isResettingPassword && (
              <p className="text-gray-600">
                {/* {isRegistering ? "Already have an account?" : "Don't have an account?"} */}
                {/* <span
                  className="text-[#fcaf17] ml-1 cursor-pointer font-semibold hover:underline"
                  onClick={() => setIsRegistering(!isRegistering)}
                >
                  {isRegistering ? "Login" : "Register"}
                </span> */}
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default MainContainer;